import {
  Users,
  Phone,
  Search,
  Mail,
  Handshake,
  Trophy,
  CircleX,
  Package,
  Circle,
} from "lucide-react";

/**
 * Maps a workflow stage name to a Lucide icon component.
 * Falls back to a generic Circle icon for unknown stage names.
 */
export function getStageIcon(stageName) {
  const normalized = (stageName || "").trim().toLowerCase();

  const map = {
    "new lead": Users,
    "contact attempt": Phone,
    "discovery call": Search,
    "proposal sent": Mail,
    negotiation: Handshake,
    won: Trophy,
    lost: CircleX,
    closed: Package,
  };

  return map[normalized] || Circle;
}

/**
 * Rotating, on-theme accent styles used for the stage icon badges.
 * Only uses colors from the approved Tailwind theme (primary / secondary / theme-*).
 */
const ACCENT_STYLES = [
  { bg: "bg-primary/10", text: "text-primary" },
  { bg: "bg-secondary/10", text: "text-secondary" },
  { bg: "bg-theme-100", text: "text-theme-600" },
];

export function getStageAccent(index) {
  return ACCENT_STYLES[index % ACCENT_STYLES.length];
}

/**
 * Returns true if the given owner is assigned to the given stage id.
 */
export function isOwnerAssignedToStage(owner, stageId) {
  return Array.isArray(owner?.stage_ids) && owner.stage_ids.includes(stageId);
}

/**
 * Immutably toggles a stage id in/out of a single owner's stage_ids,
 * identified by the owner's user.id. Returns a brand new owners array;
 * never mutates the original array or any nested object.
 */
export function toggleOwnerStage(owners, userId, stageId) {
  return owners.map((owner) => {
    if (owner.user.id !== userId) return owner;

    const alreadyAssigned = owner.stage_ids.includes(stageId);
    const nextStageIds = alreadyAssigned
      ? owner.stage_ids.filter((id) => id !== stageId)
      : [...owner.stage_ids, stageId];

    return {
      ...owner,
      stage_ids: nextStageIds,
    };
  });
}

/**
 * Builds initials (e.g. "John Smith" -> "JS") for avatar fallbacks.
 */
export default function getInitials(name = "") {
  const parts = name.trim().split(/\s+/).filter(Boolean);

  if (!parts.length) return "?";

  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase();
  }

  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}